import { PaymentMethod } from '@lucidtech/las-sdk-browser';
import { PaymentElement as StripePaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { useSessionStorage } from '@uidotdev/usehooks';
import { use, useCallback, useMemo, useState } from 'react';

import { Button, Flex, Text } from '@/components/core';
import { SaveIcon } from '@/components/icons';
import { CreditCard } from '@/components/ui';
import { toPaymentMethodId, useGetPaymentMethod } from '@/hooks/api';
import { useOrganizationStore } from '@/store';
import { getPendingPaymentMethodKey } from '@/utils';

export type UseStripePaymentOpts = {
  planId: string;
  redirectUrl: '/_auth/settings/billing' | '/_auth/settings/plans';
  getPaymentMethodPromise?: Promise<PaymentMethod> | null;
  closeModal?: () => void;
};

export const useStripePayment = ({
  planId,
  redirectUrl,
  getPaymentMethodPromise,
  closeModal,
}: UseStripePaymentOpts) => {
  const updateOrganization = useOrganizationStore((state) => state.update);
  const organizationId = useOrganizationStore((state) => state.organizationId);
  const paymentMethodId = useOrganizationStore((state) => state.paymentMethodId);
  const { data: currentPaymentMethod } = useGetPaymentMethod(toPaymentMethodId(paymentMethodId));

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [paymentMethod, _] = useSessionStorage<PaymentMethod | undefined>(
    getPendingPaymentMethodKey(organizationId),
    getPaymentMethodPromise ? use(getPaymentMethodPromise) : undefined
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const stripe = useStripe();
  const elements = useElements();

  const updateOrganizationWithPlan = useCallback(() => {
    const paymentMethodId = currentPaymentMethod?.paymentMethodId || paymentMethod?.paymentMethodId;
    if (paymentMethodId) {
      setIsSubmitting(true);

      updateOrganization({ paymentMethodId, planId });

      closeModal?.();
    }
  }, [closeModal, currentPaymentMethod?.paymentMethodId, paymentMethod?.paymentMethodId, planId, updateOrganization]);

  const [stripeError, setStripeError] = useState<Error | null>(null);

  const confirmPaymentDetails = useCallback(() => {
    if (stripe && elements) {
      setIsSubmitting(true);
      setStripeError(null);

      stripe
        .confirmSetup({
          elements,
          confirmParams: {
            return_url: redirectUrl.replace('/_auth', import.meta.env.VITE_APP_URL),
          },
        })
        .catch((error) => {
          console.error(error);
          setStripeError(error);
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    }
  }, [elements, redirectUrl, stripe]);

  return useMemo(() => {
    let PaymentElement;
    let PaymentButton;

    const paymentDetails = currentPaymentMethod?.details || paymentMethod?.details;
    if (paymentDetails) {
      PaymentElement = () => (
        <Flex className="h-fit w-full rounded-md border border-gray-200 bg-white p-4">
          <CreditCard details={paymentDetails} />
        </Flex>
      );
      PaymentButton = () => (
        <Button variant="primary" isDisabled={isSubmitting} onPress={updateOrganizationWithPlan}>
          {isSubmitting ? <SaveIcon /> : null}
          <Text>Upgrade</Text>
        </Button>
      );
    } else {
      PaymentElement = StripePaymentElement;
      PaymentButton = () => (
        <>
          {stripeError ? (
            <Text variant="error" size="xs">
              Something went wrong. Please contact our{' '}
              <span onClick={() => window._chatlio?.open()} className="cursor-pointer font-semibold underline">
                chat
              </span>{' '}
              or <span className="font-semibold">support@cradl.ai</span> if the problem persists.
            </Text>
          ) : null}
          <Button variant="primary" isDisabled={isSubmitting || !elements || !stripe} onPress={confirmPaymentDetails}>
            {isSubmitting ? <SaveIcon /> : null}
            <Text>Confirm Payment Details</Text>
          </Button>
        </>
      );
    }

    return {
      PaymentElement,
      PaymentButton,
      isSubmitting,
    };
  }, [
    confirmPaymentDetails,
    currentPaymentMethod?.details,
    elements,
    isSubmitting,
    paymentMethod?.details,
    stripe,
    stripeError,
    updateOrganizationWithPlan,
  ]);
};
